import { useMemo, useState } from "react";
import { Download, Users, MessageCircle, BarChart3, TrendingUp, Crown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart, PieChart, Pie, Cell, Legend } from "recharts";
import { notify } from "@/lib/toast";
import { MS_PER_DAY } from "@/lib/constants";
import { useAdminBundle, useUserDrill } from "@/hooks/useAdminData";
import UserDrillModal, { PlanBadge } from "@/components/admin/UserDrillModal";

// ---------- Module-level constants (stable across renders) ----------
const PIE_INNER_RADIUS = 45;
const PIE_OUTER_RADIUS = 75;
const PIE_PADDING_ANGLE = 3;
const SIGNUPS_X_AXIS_INTERVAL = 3;
const CHART_TOOLTIP_STYLE = {
    background: "rgba(0,0,0,0.85)",
    border: "1px solid rgba(16,185,129,0.3)",
    borderRadius: 8,
    fontSize: 12,
};
const LEGEND_STYLE = { fontSize: 11, color: "#9ca3af" };
const BAR_RADIUS = [4, 4, 0, 0];
const PLAN_COLORS = {
    free: "#6b7280",
    growth: "#10b981",
    pro: "#a855f7",
    business: "#f59e0b",
};
const CSV_HEADER = "Name,Business,Phone,Email,Slug,Plan,Role,Provider,Signup Date,Total Scans,Total Leads,Conversion %,Last Lead";

// ---------- Helpers ----------
const USER_SEARCH_KEYS = ["name", "business", "phone", "email", "slug"];

function userMatchesQuery(u, query) {
    if (!query) return true;
    return USER_SEARCH_KEYS.some((k) => (u[k] || "").toString().toLowerCase().includes(query));
}

function userMatchesPlan(u, planFilter) {
    return planFilter === "all" || u.plan === planFilter;
}

function userMatchesFilter(u, query, planFilter) {
    return userMatchesPlan(u, planFilter) && userMatchesQuery(u, query);
}

function userToCsvRow(u) {
    const cells = [
        u.name, u.business, u.phone, u.email, u.slug, u.plan, u.role, u.auth_provider,
        u.created_at ? new Date(u.created_at).toLocaleString() : "",
        u.total_scans, u.total_leads, u.conversion + "%",
        u.last_lead_at ? new Date(u.last_lead_at).toLocaleString() : "",
    ];
    return cells.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",");
}

function downloadUsersCsv(users) {
    const csv = [CSV_HEADER, ...users.map(userToCsvRow)].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `connexa-subscribers-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

// ---------- Sub-components ----------
function TrialBadge({ until }) {
    const days = Math.max(0, Math.ceil((new Date(until) - new Date()) / 86400000));
    if (days === 0) return null;
    return (
        <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-500/20 text-purple-300" title={`Trial ends ${new Date(until).toLocaleDateString()}`}>
            Trial · {days}d
        </span>
    );
}

function KPI({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="stat-card glass rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-brand-400" />
                <span className="text-xs text-gray-400">{label}</span>
            </div>
            <div className="text-2xl font-bold gradient-text" data-testid={testid}>{value}</div>
            <div className="text-[10px] text-gray-500 mt-1">{sub}</div>
        </div>
    );
}

function KPIGrid({ stats }) {
    const paidPlans = (stats.plan_counts.growth || 0) + (stats.plan_counts.pro || 0) + (stats.plan_counts.business || 0);
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <KPI icon={Users} label="Total Users" value={stats.total_users} sub={`${stats.today_signups} today`} testid="kpi-users" />
            <KPI icon={MessageCircle} label="Total Leads" value={stats.total_leads} sub={`${stats.today_leads} today`} testid="kpi-leads" />
            <KPI icon={BarChart3} label="Total Scans" value={stats.total_scans} sub={`${stats.today_scans} today`} testid="kpi-scans" />
            <KPI icon={TrendingUp} label="Conversion" value={`${stats.conversion}%`} sub="leads / scans" testid="kpi-conv" />
            <KPI icon={TrendingUp} label="Week Leads" value={stats.week_leads} sub="last 7 days" testid="kpi-week" />
            <KPI icon={Crown} label="Paid Plans" value={paidPlans} sub="non-free users" testid="kpi-paid" />
        </div>
    );
}

function SignupsChart({ series }) {
    return (
        <div className="glass rounded-2xl p-6 lg:col-span-2">
            <h3 className="font-semibold text-lg mb-4">Signups (last 30 days)</h3>
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={series}>
                        <defs>
                            <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="label" stroke="#6b7280" fontSize={10} interval={SIGNUPS_X_AXIS_INTERVAL} />
                        <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Area type="monotone" dataKey="count" stroke="#10b981" fill="url(#signupGrad)" strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function PlanMixChart({ planPie }) {
    return (
        <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold text-lg mb-4">Plan Mix</h3>
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={planPie} cx="50%" cy="50%" innerRadius={PIE_INNER_RADIUS} outerRadius={PIE_OUTER_RADIUS} dataKey="value" paddingAngle={PIE_PADDING_ANGLE}>
                            {planPie.map((p) => <Cell key={p.name} fill={PLAN_COLORS[p.name] || "#6b7280"} />)}
                        </Pie>
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Legend wrapperStyle={LEGEND_STYLE} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function LeadsBarChart({ series }) {
    return (
        <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold text-lg mb-4">Leads (last 7 days)</h3>
            <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="label" stroke="#6b7280" fontSize={11} />
                        <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Bar dataKey="count" fill="#10b981" radius={BAR_RADIUS} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function TopUsersPanel({ topUsers }) {
    return (
        <div className="glass rounded-2xl p-6">
            <h3 className="font-semibold text-lg mb-4">Top 10 Users (by leads)</h3>
            {topUsers.length === 0 ? (
                <p className="text-sm text-gray-500 mt-8 text-center">No leads collected yet</p>
            ) : (
                <div className="space-y-2">
                    {topUsers.map((u, i) => <TopUserRow key={u.id} index={i} user={u} />)}
                </div>
            )}
        </div>
    );
}

function TopUserRow({ index, user }) {
    return (
        <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 text-sm">
            <div className="flex items-center gap-3 min-w-0">
                <span className="text-gray-500 w-5">{index + 1}</span>
                <div className="min-w-0">
                    <div className="font-medium truncate">{user.business || user.name}</div>
                    <div className="text-xs text-gray-500 truncate">+{user.phone}</div>
                </div>
            </div>
            <span className="text-brand-400 font-bold whitespace-nowrap">{user.leads} leads</span>
        </div>
    );
}

function SubscribersToolbar({ planFilter, setPlanFilter, search, setSearch, count }) {
    return (
        <div className="p-6 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-semibold text-lg">Subscribers ({count.filtered} / {count.total})</h3>
            <div className="flex gap-2">
                <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                    className="input-field px-3 py-2 rounded-lg text-sm" data-testid="admin-plan-filter">
                    <option value="all">All plans</option>
                    <option value="free">Free</option>
                    <option value="growth">Growth</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business+</option>
                </select>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, email..."
                    className="input-field px-4 py-2 rounded-lg text-sm w-72" data-testid="admin-search" />
            </div>
        </div>
    );
}

function SubscribersTable({ users, onRowClick }) {
    if (users.length === 0) {
        return <div className="p-12 text-center text-gray-500">No subscribers match your filter</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-white/5">
                        <th className="px-5 py-4 font-medium">Subscriber</th>
                        <th className="px-5 py-4 font-medium">Contact</th>
                        <th className="px-5 py-4 font-medium">Plan</th>
                        <th className="px-5 py-4 font-medium">Scans</th>
                        <th className="px-5 py-4 font-medium">Leads</th>
                        <th className="px-5 py-4 font-medium">Conv</th>
                        <th className="px-5 py-4 font-medium">Joined</th>
                    </tr>
                </thead>
                <tbody data-testid="admin-users-tbody">
                    {users.map((u) => <SubscriberRow key={u.id} user={u} onClick={() => onRowClick(u)} />)}
                </tbody>
            </table>
        </div>
    );
}

function SubscriberRow({ user, onClick }) {
    return (
        <tr onClick={onClick} className="lead-row border-b border-white/5 cursor-pointer">
            <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-500/20 flex items-center justify-center text-brand-400 text-sm font-bold">{(user.name || "?").charAt(0).toUpperCase()}</div>
                    <div className="min-w-0">
                        <div className="font-medium text-sm flex items-center gap-1">
                            {user.name}
                            {user.role === "admin" && <Crown className="w-3 h-3 text-yellow-400" />}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{user.business || user.slug}</div>
                    </div>
                </div>
            </td>
            <td className="px-5 py-3 text-sm">
                <div className="text-gray-300">+{user.phone || "—"}</div>
                <div className="text-xs text-gray-500 truncate max-w-[180px]">{user.email || "—"}</div>
            </td>
            <td className="px-5 py-3">
                <PlanBadge plan={user.plan} />
                {user.trial_until && <TrialBadge until={user.trial_until} />}
            </td>
            <td className="px-5 py-3 text-sm font-medium text-gray-300">{user.total_scans}</td>
            <td className="px-5 py-3 text-sm font-medium text-brand-400">{user.total_leads}</td>
            <td className="px-5 py-3 text-sm text-gray-400">{user.conversion}%</td>
            <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</td>
        </tr>
    );
}

function AdminHeader({ onExport }) {
    return (
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/15 text-yellow-400 text-xs font-bold mb-2">
                    <Crown className="w-3.5 h-3.5" /> ADMIN PANEL
                </div>
                <h1 className="font-display font-bold text-3xl mb-1">Connexa Control Center</h1>
                <p className="text-gray-400">Subscribers, performance, and platform-wide metrics</p>
            </div>
            <button onClick={onExport} className="btn-primary px-6 py-3 rounded-xl font-semibold text-white flex items-center gap-2" data-testid="admin-export-csv">
                <Download className="w-5 h-5" /> Export Subscribers
            </button>
        </div>
    );
}

function LoadingSpinner() {
    return (
        <div className="min-h-screen pt-24 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full border-4 border-brand-500/30 border-t-brand-500 animate-spin"></div>
        </div>
    );
}

export default function AdminPanel() {
    const { stats, users } = useAdminBundle();
    const [search, setSearch] = useState("");
    const [planFilter, setPlanFilter] = useState("all");
    const [selectedUser, setSelectedUser] = useState(null);
    const drillData = useUserDrill(selectedUser);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return users.filter((u) => userMatchesFilter(u, q, planFilter));
    }, [users, search, planFilter]);

    const planPie = useMemo(() => {
        if (!stats?.plan_counts) return [];
        return Object.entries(stats.plan_counts).map(([k, v]) => ({ name: k, value: v }));
    }, [stats]);

    const count = useMemo(
        () => ({ filtered: filtered.length, total: users.length }),
        [filtered.length, users.length],
    );

    function exportUsersCSV() {
        if (users.length === 0) { notify("No users to export", "error"); return; }
        downloadUsersCsv(users);
        notify("Subscribers exported");
    }

    if (!stats) return <LoadingSpinner />;

    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient" data-testid="admin-page">
            <div className="max-w-7xl mx-auto px-6">
                <AdminHeader onExport={exportUsersCSV} />
                <KPIGrid stats={stats} />
                <ChartsRow stats={stats} planPie={planPie} />
                <SubscribersPanel
                    filtered={filtered} count={count}
                    search={search} setSearch={setSearch}
                    planFilter={planFilter} setPlanFilter={setPlanFilter}
                    onRowClick={setSelectedUser}
                />
                <p className="text-center text-xs text-gray-600 mt-6">Tip: click any row to drill into that user's full activity.</p>
            </div>
            {selectedUser && (
                <UserDrillModal user={selectedUser} data={drillData} onClose={() => setSelectedUser(null)} />
            )}
        </section>
    );
}

function ChartsRow({ stats, planPie }) {
    return (
        <>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <SignupsChart series={stats.signups_series} />
                <PlanMixChart planPie={planPie} />
            </div>
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
                <LeadsBarChart series={stats.leads_series} />
                <TopUsersPanel topUsers={stats.top_users} />
            </div>
        </>
    );
}

function SubscribersPanel({ filtered, count, search, setSearch, planFilter, setPlanFilter, onRowClick }) {
    return (
        <div className="glass rounded-2xl overflow-hidden">
            <SubscribersToolbar
                planFilter={planFilter} setPlanFilter={setPlanFilter}
                search={search} setSearch={setSearch}
                count={count}
            />
            <SubscribersTable users={filtered} onRowClick={onRowClick} />
        </div>
    );
}
